// Producer home — project list + "Create project" entry point.
//
// SLP Professional shell (redesign V2): the project list is an SLP panel with
// the compact slp-table + status lamps; page header is tool-dense. Data flow
// (React Query, role guard, router, tour hooks) unchanged.

import { EmptyState, SlpButton, SlpPanel, SlpStatusLamp, Spinner } from '@ohear/ui'
import type { SlpLampStatus } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useProducerProjects } from '../../lib/api/producer-projects'
import type { ProjectStatus, ProjectSummary } from '../../lib/api/producer-types'
import { useRequireRole } from '../../lib/auth'

export const Route = createFileRoute('/producer/')({
  component: ProducerHome,
})

// Status → SLP lamp semantics: green = healthy/running, yellow = needs
// attention (pipeline stages), gray = not started.
const STATUS_LAMP: Record<ProjectStatus, SlpLampStatus> = {
  draft: 'off',
  preprocessing: 'warn',
  preview: 'warn',
  recording: 'ok',
  qc: 'warn',
  delivery: 'ok',
  closed: 'ok',
}

function ProducerHome() {
  useRequireRole(['owner', 'member'])
  const projects = useProducerProjects()

  const list = projects.data ?? []

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-[16px] font-bold tracking-tight text-[var(--text)] m-0">
            Producer console
          </h1>
          <p className="slp-mono m-0" style={{ fontSize: 10, color: 'var(--slp-text-dim)' }}>
            {list.length === 0
              ? 'NO PROJECTS YET'
              : `${list.length} PROJECT${list.length === 1 ? '' : 'S'} IN FLIGHT`}
          </p>
        </div>
        <Link to="/producer/projects/new" data-tour="new-project" className="no-underline">
          <SlpButton>+ New project</SlpButton>
        </Link>
      </header>

      {projects.isLoading ? (
        <SlpPanel title="Projects">
          <div className="flex items-center gap-2" style={{ color: 'var(--slp-text-dim)' }}>
            <Spinner /> Loading projects…
          </div>
        </SlpPanel>
      ) : projects.isError ? (
        <SlpPanel title="Projects">
          <div className="slp-row">
            <SlpStatusLamp status="warn" />
            <span>
              Could not load projects: {projects.error.message} — check your connection and
              reload.
            </span>
          </div>
        </SlpPanel>
      ) : list.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="Create your first project to get started."
          action={
            <Link to="/producer/projects/new" className="no-underline">
              <SlpButton>+ New project</SlpButton>
            </Link>
          }
        />
      ) : (
        <SlpPanel title="Projects" bodyClassName="!p-0">
          <div className="overflow-x-auto">
            <table className="slp-table">
              <thead>
                <tr>
                  <th aria-label="Status" style={{ width: 26 }} />
                  <th>Code</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Lines</th>
                  <th style={{ textAlign: 'right' }}>Actors</th>
                  <th>Due</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {list.map((p: ProjectSummary) => {
                  const pct =
                    p.total_lines > 0 ? Math.round((p.approved_lines / p.total_lines) * 100) : 0
                  return (
                    <tr key={p.id}>
                      <td style={{ paddingLeft: 10 }}>
                        <SlpStatusLamp status={STATUS_LAMP[p.status]} />
                      </td>
                      <td className="slp-mono">{p.code}</td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>
                        <span className="slp-mono" style={{ fontSize: 10, letterSpacing: '0.08em' }}>
                          {p.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="slp-mono" style={{ textAlign: 'right' }}>
                        {p.approved_lines}/{p.total_lines}
                        {p.total_lines > 0 ? (
                          <span style={{ color: 'var(--slp-text-dim)' }}> · {pct}%</span>
                        ) : null}
                      </td>
                      <td className="slp-mono" style={{ textAlign: 'right' }}>
                        {p.active_actors}/{p.total_characters}
                      </td>
                      <td className="slp-mono" style={{ color: 'var(--slp-text-dim)' }}>
                        {p.due_date ? p.due_date.slice(0, 10) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', paddingRight: 10 }}>
                        <Link
                          to="/producer/projects/$projectId"
                          params={{ projectId: p.id }}
                          className="no-underline"
                        >
                          <SlpButton variant="secondary" style={{ height: 22, padding: '0 10px' }}>
                            Open →
                          </SlpButton>
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </SlpPanel>
      )}
    </section>
  )
}
